import React, {useState, useEffect} from "react"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Col} from "@irati/ui/src/Layout"
import Form from "@irati/ui/src/Form"
import {GroupCelebration} from "@irati/types"
import tracking from "@irati-tracking/recognition/recognition"
import {injectIntl, InjectIntlProps} from "@irati/utils/lang/injectIntl"
import Flex from "@irati/ui/src/Flex"
import RecognitionService from "@irati/recognition/services/Recognition"
import {Recognition as RecognitionField} from "@irati-web/fields/poll/votes"
import FormattedMessage from "@irati/utils/lang"
import {Body, H2, H3} from "@irati/ui/src/Typography"
import {ValidationButton} from "@irati/ui/src/FormItem"
import Button from "@irati/ui/src/Button"
import {recognitionSchema} from "@irati-web/fields/poll/votes/validation"
import {MAX_CHAR_LENGTH} from "@irati-web/fields/poll/votes/validation/recognition"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {Separator, StyledFlex} from "../RewardRecognition/styled"
import {CelebrationIcon, ProfilePicContainer} from "../../Polls/ClosedPoll/styled"
import ProfilePicGroup, {DEFAULT_MULTIPLE_PICTURE_SIZE} from "../../ProfilePicGroup"
import ScheduledDate from "../shared/layouts/ScheduledDate"
import {RecognitionRoutes} from "../../../enums/Routes"

interface AddMessageProps extends InjectIntlProps {
  scheduledGroupCelebration: GroupCelebration
  onMessageSent: () => void
  onSkip: () => void
}
const AddMessage: React.FC<AddMessageProps> = (props) => {
  const {formatMessage, scheduledGroupCelebration, intl, onSkip, onMessageSent} = props
  const theme = useTheme()
  const [message, setMessage] = useState<string>()
  const [submit, setSubmit] = useState()
  useEffect(() => {
    tracking.recognitionConfirmation({type: "scheduled", source: "greeting_card_author"})
  }, [])
  const onSubmit = async () => {
    tracking.recognitionCtaClick({
      source: "confirmation_modal",
      type: "greeting_card_add_message"
    })
    const args = {
      groupCelebrationID: scheduledGroupCelebration.uuid,
      message
    }
    const submitPromise = RecognitionService.createGroupCelebrationMessage(args)
      .then(onMessageSent)
      .finally(() => setSubmit())
    setSubmit(submitPromise)
  }

  if (!scheduledGroupCelebration?.uuid) {
    return null
  }
  const greetingLabel = `${scheduledGroupCelebration?.greeting?.label}, ${scheduledGroupCelebration?.receiver.firstName} ${scheduledGroupCelebration?.receiver.lastName}`
  const onEditClick = () => {
    tracking.recognitionCtaClick({
      source: "confirmation_modal",
      type: "greeting_card_edit"
    })
  }
  return (
    <StyledFlex>
      <Col xs={12} sm={7} padding="0">
        <Flex direction="column" alignItems="center" gap={2}>
          <ProfilePicContainer>
            <CelebrationIcon src={scheduledGroupCelebration?.greeting?.icon?.href} />
            <ProfilePicGroup
              users={[scheduledGroupCelebration?.receiver]}
              outerWrapperSize={DEFAULT_MULTIPLE_PICTURE_SIZE}
              singlePictureSize={DEFAULT_MULTIPLE_PICTURE_SIZE}
            />
          </ProfilePicContainer>
          <H2 textAlign="center">
            <FormattedMessage id="recognition.popup.createGroupCelebration.messagePopup.leftTitle" />
          </H2>
          <ScheduledDate scheduledGroupCelebration={scheduledGroupCelebration} />
          <Body>
            <FormattedMessage id={greetingLabel} />
          </Body>
          <ButtonRouterLink
            to={`${RecognitionRoutes.ACCOUNT}/my-group-celebrations/${scheduledGroupCelebration.uuid}`}
            cta
            onClick={onEditClick}>
            <FormattedMessage id="platform.button.edit" />
          </ButtonRouterLink>
        </Flex>
      </Col>
      <Col xs={12} sm={1} padding="0">
        <Flex justifyContent="center" height="100%">
          <Separator />
        </Flex>
      </Col>
      <Col xs={12} sm={7} padding="0">
        <H3 textAlign="center" marginBottom={4}>
          <FormattedMessage id="recognition.popup.createGroupCelebration.messagePopup.title" />
        </H3>
        <Body small>
          <FormattedMessage id="recognition.popup.createGroupCelebration.messagePopup.subtitle" />
        </Body>
        <Flex direction="column" justifyContent="space-between">
          <Form width="100%">
            <RecognitionField
              onChange={setMessage}
              value={message}
              minHeight={170}
              maxLength={MAX_CHAR_LENGTH}
              validation={recognitionSchema(intl)}
              placeholder={formatMessage({id: "recognition.popup.shared.comments.placeholder"})}
            />
            <Flex gap={theme.spacing.two} direction="column">
              <ValidationButton primary full onClick={onSubmit} load={submit}>
                <FormattedMessage id="recognition.popup.createGroupCelebration.messagePopup.cta.create" />
              </ValidationButton>
              <Button full onClick={onSkip}>
                <FormattedMessage id="recognition.popup.createGroupCelebration.messagePopup.cta.second" />
              </Button>
            </Flex>
          </Form>
        </Flex>
      </Col>
    </StyledFlex>
  )
}

export default injectIntl(AddMessage)
